// Forge (docs/FORGE.md, choix de Léni du 5 novembre 2026) : améliorations permanentes propres au mode Forge,
// coffres de puissance gagnés en jouant ou achetés avec des pièces gagnées en jouant, chances affichées avant
// l'ouverture. Le serveur décide de tout (tirage, prix, plafonds) : la page ne fait que montrer et demander.
import { useCallback, useEffect, useState } from 'react';
import { RARITIES, RARITY_COLOR, type Rarity } from '../../../shared/cosmetics';
import {
  FORGE_CHESTS,
  FORGE_EARNED_ODDS,
  FORGE_MAX_LEVEL,
  FORGE_MAX_POWER,
  FORGE_MINUTES_PER_CHEST,
  FORGE_PART_REFUND,
  FORGE_STEP,
  FORGE_TIME_CHESTS_PER_DAY,
  FORGE_UPGRADES,
  forgePower,
  levelCost,
  type ForgeLevels,
  type ForgeUpgradeId,
} from '../../../shared/forge';
import { apiGet, apiPost, type MeResponse } from '../game/api';
import { css } from '../game/colors';
import { formatNumber, formatPercent, t, type Key } from '../i18n';
import { Icon } from './icons';
import type { Page } from './MenuShell';
import { ConfirmButton, RarityTag, rarityName, reqId } from './PaintPage';

interface ForgeState {
  levels: ForgeLevels;
  parts: Record<ForgeUpgradeId, number>;
  boxes: number;
  bossBoxes: number;
  minutes: number;
  todayBoxes: number;
  coins: number;
  earnedCoins: number;
}

type Parts = Partial<Record<ForgeUpgradeId, number>>;
interface HistoryLine {
  source: string;
  rarity: Rarity;
  parts: Parts;
  refund: number;
  at: string;
}
interface ForgeData {
  state: ForgeState | null;
  history: HistoryLine[];
  account: boolean;
  verified: boolean;
}

export const upgradeName = (id: ForgeUpgradeId) => t(`forge_up_${id}` as Key);
const pct = (x: number) => formatPercent(Math.round(x * 1000) / 10);
const partsText = (p: Parts) =>
  FORGE_UPGRADES.filter((id) => p[id])
    .map((id) => `${upgradeName(id)} +${formatNumber(p[id]!)}`)
    .join(' · ');

export function ForgePage({ me, onChanged, go }: { me: MeResponse | null; onChanged: () => void; go: (p: Page) => void }) {
  const [data, setData] = useState<ForgeData | null>(null);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [reveal, setReveal] = useState<{ rarity: Rarity; parts: Parts; refund: number } | null>(null);
  const load = useCallback(async () => {
    try {
      setData(await apiGet<ForgeData>('/api/forge'));
    } catch {
      /* panne : la page reste en lecture */
    }
  }, []);
  useEffect(() => {
    void load();
  }, [me?.account?.publicId, load]);

  const st = data?.state ?? null;
  const levels = st?.levels ?? ({ blindage: 0, canon: 0, chargeur: 0, moteur: 0, belier: 0, reparation: 0 } as ForgeLevels);
  const power = forgePower(levels);
  const canAct = !!data?.account && !!data.verified;
  const fail = (r: string) =>
    setMsg({
      text: t(
        r === 'funds' ? 'forge_err_funds' : r === 'earned' ? 'forge_err_earned' : r === 'parts' ? 'forge_err_parts' : r === 'verify' ? 'paint_err_verify' : r === 'frozen' ? 'paint_err_frozen' : r === 'max' ? 'forge_max' : r === 'none' ? 'forge_err_none' : 'paint_err',
      ),
      ok: false,
    });

  const upgrade = async (id: ForgeUpgradeId) => {
    setMsg(null);
    const r = await apiPost<{ result: string; state: ForgeState }>('/api/forge/upgrade', { upgrade: id, requestId: reqId() });
    if (r.status !== 200 || r.data.result !== 'ok') return fail(r.data?.result ?? 'error');
    setData((d) => (d ? { ...d, state: r.data.state } : d));
    setMsg({ text: t('forge_upgraded', { name: upgradeName(id), n: r.data.state.levels[id] }), ok: true });
    onChanged();
  };
  const open = async (source: string) => {
    setMsg(null);
    setReveal(null);
    const r = await apiPost<{ result: string; rarity?: Rarity; parts?: Parts; refund?: number; state: ForgeState; history: HistoryLine[] }>('/api/forge/chest', { source, requestId: reqId() });
    if (r.status !== 200 || r.data.result !== 'ok' || !r.data.rarity) return fail(r.data?.result ?? 'error');
    setReveal({ rarity: r.data.rarity, parts: r.data.parts ?? {}, refund: r.data.refund ?? 0 });
    setData((d) => (d ? { ...d, state: r.data.state, history: r.data.history } : d));
    onChanged();
  };

  return (
    <div className="page forge-page">
      <section className="plate forge-head" aria-labelledby="forge-title">
        <h1 className="plate-title" id="forge-title">
          {t('forge_title')}
        </h1>
        <p className="wip">{t('forge_intro')}</p>
        <div className="forge-power" data-testid="forge-power">
          <span className="eyebrow">{t('forge_power')}</span>
          <span className="forge-power-n display num">{power}</span>
          <span className="forge-power-of num">/ {FORGE_MAX_POWER}</span>
        </div>
        <span className="bar" aria-hidden>
          <span className="fill fill-xp" style={{ width: `${(power / FORGE_MAX_POWER) * 100}%` }} />
        </span>
        <p className="wip">{t('forge_fairNote')}</p>
        {st && (
          <p className="wallet-line">
            {t('forge_wallet', { c: formatNumber(st.coins), e: formatNumber(st.earnedCoins) })}
          </p>
        )}
        {!canAct && (
          <div className="forge-guest">
            <p className="form-msg">{data?.account ? t('paint_err_verify') : t('forge_needAccount')}</p>
            {!data?.account && (
              <button type="button" className="btn btn-primary btn-small" onClick={() => go('profile')}>
                {t('account')}
              </button>
            )}
          </div>
        )}
        <p className={msg ? `form-msg ${msg.ok ? 'ok' : ''}` : undefined} role="status" aria-live="polite">
          {msg?.text}
        </p>
        {/* Développement seulement (absent du site en ligne, refusé par le serveur de production). */}
        {import.meta.env.DEV && canAct && (
          <button
            type="button"
            className="btn btn-small btn-ghost"
            data-testid="forge-dev"
            onClick={() =>
              void apiPost<{ state: ForgeState }>('/api/dev/forge', {}).then((r) => {
                if (r.status === 200) setData((d) => (d ? { ...d, state: r.data.state } : d));
              })
            }
          >
            Test local : +20 000 pièces, +3 coffres, +1 coffre de boss, +100 pièces détachées
          </button>
        )}
      </section>

      <section className="plate forge-upgrades" aria-labelledby="forge-up-title">
        <h2 className="plate-title" id="forge-up-title">
          {t('forge_upgrades')}
        </h2>
        <div className="forge-grid">
          {FORGE_UPGRADES.map((id) => {
            const lv = levels[id];
            const stock = st?.parts[id] ?? 0;
            const max = lv >= FORGE_MAX_LEVEL;
            const cost = max ? null : levelCost(lv + 1);
            const enough = !!cost && stock >= cost.parts && (st?.earnedCoins ?? 0) >= cost.coins;
            return (
              <article key={id} className={`forge-card ${max ? 'max' : ''}`} data-testid={`forge-${id}`}>
                <h3 className="forge-card-name">{upgradeName(id)}</h3>
                <p className="wip">{t(`forge_eff_${id}` as Key, { p: pct(FORGE_STEP[id]) })}</p>
                <div className="forge-pips" role="img" aria-label={t('forge_levelOf', { n: lv, max: FORGE_MAX_LEVEL })}>
                  {Array.from({ length: FORGE_MAX_LEVEL }, (_, i) => (
                    <i key={i} className={i < lv ? 'on' : ''} />
                  ))}
                </div>
                <p className="forge-card-line num">
                  {t('forge_levelOf', { n: lv, max: FORGE_MAX_LEVEL })} · +{pct(FORGE_STEP[id] * lv)} %
                </p>
                <p className="forge-card-line num">{t('forge_stock', { n: formatNumber(stock) })}</p>
                {cost ? (
                  <>
                    <p className="forge-card-line num">{t('forge_cost', { p: formatNumber(cost.parts), c: formatNumber(cost.coins) })}</p>
                    <ConfirmButton label={t('forge_upgrade')} confirm={t('forge_upgradeConfirm', { n: lv + 1 })} disabled={!canAct || !enough} onConfirm={() => upgrade(id)} />
                  </>
                ) : (
                  <p className="forge-card-line ok">
                    <Icon name="check" size={14} /> {t('forge_max')}
                  </p>
                )}
              </article>
            );
          })}
        </div>
        <p className="wip">{t('forge_nextGame')}</p>
      </section>

      <section className="plate forge-chests" aria-labelledby="forge-chest-title">
        <h2 className="plate-title" id="forge-chest-title">
          {t('forge_chests')}
        </h2>
        <div className="forge-earned">
          <div>
            <h3 className="eyebrow">{t('forge_earnedTitle')}</h3>
            <p className="wip">{t('forge_earnRule', { m: FORGE_MINUTES_PER_CHEST, d: FORGE_TIME_CHESTS_PER_DAY })}</p>
            {st && <p className="num">{t('forge_progress', { m: st.minutes, max: FORGE_MINUTES_PER_CHEST, d: st.todayBoxes, dmax: FORGE_TIME_CHESTS_PER_DAY })}</p>}
            <table className="chest-odds">
              <caption className="eyebrow">{t('paint_chestOdds')}</caption>
              <tbody>
                {RARITIES.map((r) => (
                  <tr key={r}>
                    <th scope="row">
                      <RarityTag r={r} />
                    </th>
                    <td className="num">{formatPercent(FORGE_EARNED_ODDS[r] / 10)} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="forge-earned-btns">
            <p className="num" data-testid="forge-boxes">
              {t('forge_boxes', { n: formatNumber(st?.boxes ?? 0) })}
            </p>
            <button type="button" className="btn btn-primary btn-small" disabled={!canAct || !st?.boxes} onClick={() => void open('box')} data-testid="forge-open-box">
              {t('forge_open')}
            </button>
            <p className="num">{t('forge_bossBoxes', { n: formatNumber(st?.bossBoxes ?? 0) })}</p>
            <button type="button" className="btn btn-small" disabled={!canAct || !st?.bossBoxes} onClick={() => void open('boss')}>
              {t('forge_open')}
            </button>
          </div>
        </div>

        <table className="forge-table">
          <caption className="eyebrow">{t('forge_contentTitle')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('forge_colRarity')}</th>
              <th scope="col">{t('forge_colParts')}</th>
              <th scope="col">{t('forge_colUpgrades')}</th>
              <th scope="col">{t('forge_colPrice')}</th>
            </tr>
          </thead>
          <tbody>
            {RARITIES.map((r) => {
              const c = FORGE_CHESTS[r];
              return (
                <tr key={r}>
                  <th scope="row">
                    <RarityTag r={r} />
                  </th>
                  <td className="num">{formatNumber(c.parts)}</td>
                  <td className="num">{c.upgrades}</td>
                  <td>
                    {c.price === null ? (
                      <span className="wip">{t('forge_notSold')}</span>
                    ) : (
                      <ConfirmButton
                        label={t('paint_coins', { n: formatNumber(c.price) })}
                        confirm={t('paint_chestConfirm', { n: formatNumber(c.price) })}
                        disabled={!canAct || (st?.earnedCoins ?? 0) < c.price}
                        onConfirm={() => open(`buy:${r}`)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="wip">{t('forge_contentRule', { n: FORGE_PART_REFUND })}</p>
        <p className="wip">{t('forge_earnedOnly')}</p>

        {reveal && (
          <div className="chest-reveal" role="status" data-testid="forge-reveal" style={{ borderColor: css(RARITY_COLOR[reveal.rarity]) }}>
            <RarityTag r={reveal.rarity} />
            <strong>{reveal.refund ? t('forge_refunded', { n: formatNumber(reveal.refund) }) : partsText(reveal.parts)}</strong>
          </div>
        )}
        {data && data.history.length > 0 && (
          <>
            <h3 className="eyebrow">{t('paint_chestHistory')}</h3>
            <ul className="chest-history">
              {data.history.map((h, i) => (
                <li key={i}>
                  <strong style={{ color: css(RARITY_COLOR[h.rarity]) }}>{rarityName(h.rarity)}</strong> · {h.refund ? t('forge_refunded', { n: formatNumber(h.refund) }) : partsText(h.parts)}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
